import type { PrinterSchemaWithId } from "@repo/validators";
import type { z } from "zod";
import ReceiptPrinterEncoder from "@point-of-sale/receipt-printer-encoder";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { PrinterSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Printer } from "~/providers/printing-provider";
import type { ManagerResult } from "~/types";
import { usePlatform } from "~/hooks/use-platform";
import { usePrinter } from "~/hooks/use-printer";
import { api } from "~/lib/api";
import { useAlertStore } from "~/stores/use-alert-store";
import { usePrinterStore } from "~/stores/use-printer-store";
import { usePermissionManager } from "./use-permission-manager";

export const usePrinterManager = () => {
  const { t } = useTranslation("common");

  const { platform } = usePlatform();

  const { sendToPrinter, getPrinterLanguage } = usePrinter();

  const { setPrinterDialogOpen, setSelectedPrinter } = usePrinterStore();

  const { hasPermission } = usePermissionManager();

  const { showDialog } = useAlertStore();

  const { mutate: createPrinter } = api.printer.createPrinter.useMutation();

  const { mutate: updatePrinter } = api.printer.updatePrinter.useMutation();

  const { mutate: deletePrinter } = api.printer.deletePrinter.useMutation();

  const createPrinterHandler = async (
    printer: z.infer<ReturnType<typeof PrinterSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_printer,
      label: t("Permissions.create_printer_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof PrinterSchema>>>
    >((resolve) => {
      if (!permission) {
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });
      }
      createPrinter(printer, {
        onSuccess() {
          toast.success(
            capitalize(
              t("create_item_success", { item: t("Printer.printer") }),
            ),
          );
          return resolve({
            data: true,
          });
        },
        onError() {
          toast.error(
            capitalize(t("create_item_error", { item: t("Printer.printer") })),
          );
          return resolve({
            error: {
              path: "root",
              message: capitalize(
                t("create_item_error", {
                  item: t("Printer.printer"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const updatePrinterHandler = async (
    printer: z.infer<typeof PrinterSchemaWithId>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_printer,
      label: t("Permissions.update_printer_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof PrinterSchema>>>
    >((resolve) => {
      if (!permission) {
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });
      }
      updatePrinter(printer, {
        onSuccess() {
          toast.success(
            capitalize(
              t("update_item_success", { item: t("Printer.printer") }),
            ),
          );
          return resolve({
            data: true,
          });
        },
        onError() {
          toast.error(
            capitalize(t("update_item_error", { item: t("Printer.printer") })),
          );
          return resolve({
            error: {
              path: "root",
              message: capitalize(
                t("update_item_error", {
                  item: t("Printer.printer"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const deletePrinterHandler = async (id: string) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.delete_printer,
      label: t("Permissions.delete_printer_label"),
    });

    if (!permission) return false;

    const result = await showDialog({
      title: t("Printer.before_delete_title"),
      message: t("Printer.before_delete_message"),
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });

    if (result === "cancel") return false;

    return new Promise<boolean>((resolve) => {
      deletePrinter(id, {
        onSuccess() {
          toast.success(
            capitalize(
              t("delete_item_success", { item: t("Printer.printer") }),
            ),
          );
          resolve(true);
        },
        onError() {
          toast.error(
            capitalize(t("delete_item_fail", { item: t("Printer.printer") })),
          );
          resolve(false);
        },
      });
    });
  };

  const openPrinterDialog = async (printer?: Printer) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_printer,
      label: t("Permissions.create_printer_label"),
    });

    if (!permission) return false;

    setSelectedPrinter(printer);
    setPrinterDialogOpen(true);
  };

  const getSystemPrinters = async () => {
    if (platform !== "DESKTOP") return [];
    return await window.vrodex.getConnectedPrinters();
  };

  const testPrinter = (printer: z.infer<ReturnType<typeof PrinterSchema>>) => {
    const parsed = PrinterSchema().safeParse(printer);

    if (parsed.error) {
      console.error(parsed.error);
      return;
    }

    const encoder = new ReceiptPrinterEncoder({
      language: getPrinterLanguage(printer.language),
      columns: printer.width,
      imageMode: "raster",
    });

    const result = encoder
      .initialize()
      .codepage("windows1252")
      .newline()
      .newline()
      .newline()
      .align("center")
      .height(2)
      .width(2)
      .line("Test Success!")
      .newline()
      .newline()
      .newline()
      .newline()
      .newline()
      .newline()
      .newline()
      .newline()
      .newline()
      .cut("partial")
      .encode();

    void sendToPrinter(result, {
      connectionType: printer.connectionType,
      systemName: printer.systemName,
      ipAddress: printer.ipAddress,
      port: printer.port,
    });
  };

  const testDrawer = (printer: z.infer<ReturnType<typeof PrinterSchema>>) => {
    const parsed = PrinterSchema().safeParse(printer);
    if (parsed.error) return;

    const encoder = new ReceiptPrinterEncoder({
      language: getPrinterLanguage(printer.language),
      columns: printer.width,
    });

    encoder.pulse();

    const encoded = encoder.encode();

    void sendToPrinter(encoded, {
      connectionType: printer.connectionType,
      systemName: printer.systemName,
      ipAddress: printer.ipAddress,
      port: printer.port,
    });
  };

  return {
    openPrinterDialog,
    getSystemPrinters,
    testPrinter,
    testDrawer,
    createPrinter: createPrinterHandler,
    updatePrinter: updatePrinterHandler,
    deletePrinter: deletePrinterHandler,
  };
};
